import { uuid } from "@/globals";

import { AbstractSingleton } from "./abstract.singleton";

export class Progress {
    protected threadId;
    protected timer: number | undefined;

    public constructor(progressId: string) {
        this.threadId = progressId;
    }

    protected finallyCb: (() => void) | undefined = undefined;

    public run(step: number): this {
        this.timer = window.setInterval(() => {
            this.next();
        }, step);

        return this;
    }

    public finally(cb: () => void): this {
        this.finallyCb = cb;

        return this;
    }

    public next(): void {
        progressSingleton.next(this.threadId);
    }

    public finish(delay?: number): this {
        window.setTimeout(() => {
            if (this.timer) {
                clearInterval(this.timer);
            }

            progressSingleton.finish(this.threadId);

            this.finallyCb?.();
        }, delay ?? 0);
        return this;
    }

    public isRunning(): boolean {
        return progressSingleton.hasProgress(this.threadId);
    }
}

class ProgressSingleton extends AbstractSingleton {
    protected progress = 0;
    protected threads = new Map<string, Progress>();

    protected handleChange: ((p: number) => void) | undefined = undefined;

    public createProgress(threadId?: string): Progress {
        const id = threadId ?? uuid();

        const currentProgress = this.threads.get(id);
        currentProgress?.finish();

        const progress = new Progress(id);

        this.threads.set(id, progress);

        return progress;
    }

    public hasProgress = (threadId?: string): boolean => {
        if (threadId) {
            return this.threads.has(threadId);
        } else {
            return this.threads.size > 0;
        }
    };

    public next(threadId: string): void {
        if (this.threads.has(threadId)) {
            this.progress += Math.round((100 - (this.progress || 90)) / 6);

            this.handleChange?.(this.progress);
        } else {
            console.error("Illegal threadId for processing", threadId);
        }
    }

    public finish(threadId: string): void {
        const currentProgress = this.threads.get(threadId);
        if (currentProgress) {
            currentProgress.finish();
            this.threads.delete(threadId);
            if (this.threads.size === 0) {
                this.progress = 0;
                this.handleChange?.(this.progress);
            }
        }
    }

    public finishAll(): void {
        this.threads.forEach((thread) => thread.finish());
    }

    public onChange(handleChange: (p: number) => void): void {
        this.handleChange = handleChange;
    }

    public getProgress(): number {
        return this.progress;
    }
}

export const progressSingleton = new ProgressSingleton();
